import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Col, Form, Row, Button, Container, Spinner } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import "./WorkItemCreate.css";

import { toast } from "react-toastify";
import moment from "moment";
import { useProperty } from "../../data/PropertyHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import useLogger from "../../util/Logger";
import { useAppointmentSlots } from "../../data/AppointmentSlotHook";
import { useWorkItemAgreements } from "../../data/WorkItemAgreementsHook";
import { useWorkItemCategories } from "../../data/WorkItemCategoriesHook";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { isWorkOrderClosed } from "../../data/WorkOrderStatus";
import WorkOrderAddress from "../workOrder/WorkOrderAddress";
import { useWorkOrderItems } from "../../data/WorkItemHook";
import WorkItemEdit from "./WorkItemEdit";
import { useWorkItemTemplates } from "../../data/WorkItemTemplatesHook";
import SpinnerButton from "../shared/Spinnerbutton";
import { useProjects } from "../../data/ProjectsHook";

function WorkItemCreate() {
  const { propertyId, workOrderId } = useParams();
  const propertyRepository = useProperty();
  const [property, setProperty] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [idempotencyGuid, setIdempotencyGuid] = useState();
  const workOrdersRepository = useWorkOrders();
  const workItemsRepository = useWorkOrderItems();
  const logger = useLogger();
  const appointmentSlotsRepository = useAppointmentSlots();
  const workItemAgreementsRepository = useWorkItemAgreements();
  const workItemTemplatesRepository = useWorkItemTemplates();
  const workItemCategoryRepository = useWorkItemCategories();
  const projectsRepository = useProjects();
  const breadcrumbRepository = useBreadcrumb();
  const navigate = useNavigate();

  useEffect(() => {
    const runMe = async () => {
      try {
        logger.debug("WorkItemCreate.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}`,
            name: "work order",
          },
          {
            route: `/property/${propertyId}/workOrder/${workOrderId}/workItem/create`,
            name: "create work item",
          },
        ]);

        // Load all required data for selects

        const [
          retrievedProperty,
          appointmentSlots,
          workItemAgreements,
          workItemCategories,
          projects,
        ] = await Promise.all([
          propertyRepository.getProperty(propertyId),
          appointmentSlotsRepository.updateAppointmentSlots(),
          workItemAgreementsRepository.updateWorkItemAgreements(),
          workItemCategoryRepository.updateWorkItemCategories(true),
          projectsRepository.updateProjects(),
        ]);

        setProperty(retrievedProperty);

        const defaultAgreement =
          workItemAgreements.find((wia) => wia.id === "WIA001") ||
          workItemAgreements[0];

        const defaultWorkItemCategory =
          workItemCategories.find((t) => t.name === "Investigation") ||
          workItemCategories[0];

        const retrievedWorkOrder =
          await workOrdersRepository.getWorkOrderForUpdate(
            propertyId,
            workOrderId,
            workItemCategories
          );

        const filters = {
          Maintenance: ["DomesticCMV"],
          Reactive: ["DomesticFieldReactive", "Disconnection", "Reconnection"],
          Project: [
            "Project Property (Installation)",
            "Project Property (Asset)",
          ],
        };

        let chosenFilter = filters[retrievedWorkOrder.type] || [
          "DomesticFieldReactive",
        ];

        if (retrievedWorkOrder.category === "WOC-22") {
          chosenFilter = [...chosenFilter, "DomesticCMV"];
        }

        const templates =
          await workItemTemplatesRepository.getWorkItemTemplates(chosenFilter);

        const defaultTemplate = chosenFilter.includes("DomesticFieldReactive")
          ? templates.find((t) => t.ref === "DomesticFieldReactive")
          : templates[0];

        if (isWorkOrderClosed(retrievedWorkOrder)) {
          throw new Error(
            `Illegal attempt to edit closed work order for property '${
              retrievedProperty.id
            }' and work order '${JSON.stringify(workOrderId)}'`
          );
        }

        setWorkOrder(retrievedWorkOrder);

        const retrievedProject = projects.find(
          ({ projectReference }) =>
            projectReference === retrievedWorkOrder.projectId
        );

        workItemsRepository.clearEntry(
          null,
          appointmentSlots[0].id,
          appointmentSlots[0].name,
          defaultAgreement.id,
          defaultAgreement.name,
          defaultWorkItemCategory.id,
          defaultWorkItemCategory.name,
          defaultTemplate.id,
          defaultTemplate.ref,
          "",
          retrievedProject?.projectReference,
          retrievedProject?.projectName,
          retrievedProperty.redFlag,
          retrievedProperty.propertyAccessNotes,
          "",
          "",
          ""
        );

        setIsLoading(false);
      } catch (err) {
        logger.error(
          "WorkItemCreate.useEffect",
          `An error occurred while loading work order '${workOrderId}' ${err}`
        );
        toast.error(
          "An error occurred while loading the work order. Please try again."
        );
        navigate("/");
      }
    };
    runMe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIdempotencyGuid(uuidv4());
  }, []);

  const handleSubmit = async (event) => {
    try {
      setIsSaving(true);

      const form = event.currentTarget;
      event.preventDefault();
      event.stopPropagation();

      const current = workItemsRepository.getEntry();
      if (form.checkValidity() === false) {
        setValidated(true);
        toast.error(
          "There are errors on your form. Please check each field and try again."
        );
      } else if (
        !current.preferredAppointmentDate &&
        current.appointmentConfirmedTitle !== "Not Agreed" &&
        current.workitemtemplateref !== "DomesticFieldReactive" &&
        current.workitemtemplateref !== "Reconnection" &&
        current.workitemtemplateref !== "Disconnection"
      ) {
        setValidated(true);
        toast.error("Please select a date from the calendar.");
      } else {
        // Save the work order item
        await workItemsRepository.createNewWorkOrderItem(workOrder, {
          ...current,
          idempotencyGuid,
        });

        toast.success("The Work Item was created successfully.");
        navigate(`/property/${propertyId}/workOrder/${workOrderId}`);
      }
    } catch (err) {
      logger.error(
        "WorkItemCreate.handleSubmit",
        `API patch work item failed ${err}`
      );
      toast.error("The work item could not be created. Please try again.");
    }
    setIsSaving(false);
  };

  const workOrderItem = workItemsRepository.getEntry();

  return !isLoading && workOrder && workOrderItem ? (
    <Container className="coreContent">
      <h2>Create Work Item</h2>
      <WorkOrderAddress property={property} />

      <Row className="mt-4">
        <Col xs={3}>
          <Form.Label className="form-label-heading">
            Work Order Reference
          </Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Created</Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Type</Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Category</Form.Label>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.reference} />
        </Col>
        <Col xs={3}>
          <Form.Control
            type="text"
            readOnly
            value={
              workOrder.createdOn
                ? moment(workOrder.createdOn).format("DD/MM/YYYY h:mm a")
                : ""
            }
          />
        </Col>
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.type} />
        </Col>
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.categoryName} />
        </Col>
      </Row>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <div className="entry-container">
          <WorkItemEdit />
        </div>

        <div className="work-item-create-buttons-container">
          <Row xs="auto">
            <Col className="work-item-create-button">
              <Link to={`/property/${property.id}/workorder/${workOrderId}`}>
                <Button variant="secondary">Back to work order</Button>
              </Link>
            </Col>
            <Col>
              <SpinnerButton
                text="Save"
                variant="primary"
                action={() => document.getElementById("updateSubmit").click()}
                waiting={isSaving}
              />
            </Col>
            <Col>
              <Button
                id="updateSubmit"
                type="submit"
                style={{ visibility: "hidden" }}
              >
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </Container>
  ) : (
    <Container className="coreContent">
      <Spinner
        animation="border"
        role="status"
        style={{
          marginTop: "200px",
          marginLeft: "50%",
          marginBottom: "200px",
        }}
      >
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </Container>
  );
}

export default WorkItemCreate;
